import type { TableMenuItem } from '@/components_deprecated/Table'
import {
  Table as BaseTable,
  SortableHeader,
  TableMenu,
} from '@/components_deprecated/Table'
import { UserCell, VehicleCell } from '@/components_deprecated/Table/cells'
import type { IssueOrderFields } from '@/modules/issues'
import {
  DELETE_ISSUE_MUTATION,
  TOGGLE_ISSUE_ARCHIVED_MUTATION,
  TOGGLE_ISSUE_CLOSED_MUTATION,
} from '@/modules/issues/mutations'
import { getNodes } from '@/utils/graphql'
import {
  useAdmin,
  useCompany,
  useConfirm,
  useInfiniteScroll,
  useOnErrorAuto,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation, useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useDebounce, useTranslate } from '@ur-solutions/react-hooks'
import { addDays, formatISO, startOfDay } from 'date-fns'
import React, { useCallback, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { ALL_ISSUES_QUERY } from '../../queries'
import type { CloseIssueForm, IssuesTableFilter } from '../../types'
import type {
  IssueNodeForAllIssues,
  ToggleIssueArchivedMutation,
  ToggleIssueArchivedMutationVariables,
  ToggleIssueClosedMutation,
  ToggleIssueClosedMutationVariables,
} from '../../types.graphl'
import { CloseIssuePrompt } from './CloseIssueModal'
import {
  IssueCategoryCell,
  IssueDateCell,
  IssueReferenceCell,
  IssueStatusCell,
} from './components'
import { IssuesCards } from './IssuesCards'

const Table = styled(BaseTable)``

interface RowProps {
  closed: boolean
}

const Row = styled.tr<RowProps>`
  background: ${props =>
    props.closed ? `${props.theme.colors.secondary}07` : 'inherit'};
`

interface IssuesTableProps {
  filter: IssuesTableFilter

  onSort: (orderBy: IssueOrderFields) => void
  onFilterUpdate?: (info: { count: number | undefined }) => void
}

export const IssuesTable: React.FC<IssuesTableProps> = ({
  filter,

  onSort,
  onFilterUpdate,
}) => {
  const translations = useTranslate({
    date: 'common.date',
    freightCarrier: 'common.freight-carrier',
    reference: ['issues.reference', { abbreviate: false }],
    employee: 'common.submitter',
    category: 'common.category',
    status: 'common.status',

    open: 'common.open-alt',
    archived: 'common.archived',
    closed: 'common.closed',

    noIssues: 'issues.no-issues',
    noResults: 'common.no-results',

    editIssue: 'issues.edit-issue',
    closeIssue: 'issues.close-issue',
    reopenIssue: 'issues.reopen-issue',
    archiveIssue: 'issues.archive-issue',
    restoreIssue: 'issues.restore-issue',
    deleteIssue: 'issues.delete-issue',
    deleteIssueConfirm: 'issues.delete-issue-confirm',
    started: 'issues.started',
    severityGrades: {
      LOW: 'common.low',
      MEDIUM: 'common.medium',
      HIGH: 'common.high',
    },
    severity: 'common.severity',
    riskAspect: 'common.risk-aspect',
  })

  const navigate = useNavigate()

  const company = useCompany()
  const me = useUser()
  const admin = useAdmin()
  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const { hasPermissionsAndMe, hasPermissionsAndNotMe, hasPermissions } =
    usePermissions()
  const onErrorAuto = useOnErrorAuto()

  const debouncedQuery = useDebounce(filter.query)
  const debouncedExcludeCategories = useDebounce(filter.excludeCategories, 500)
  const debouncedStatuses = useDebounce(filter.statuses)
  const debouncedArchived = useDebounce(filter.archived)
  const debouncedTags = useDebounce(filter.tags, 500)

  const canEdit = (issue: IssueNodeForAllIssues) =>
    admin || hasPermissionsAndMe(issue.user, PERMISSIONS.issues.change.issue)

  const userCanCloseIssue = (issue: IssueNodeForAllIssues) =>
    admin || hasPermissionsAndNotMe(issue.user, PERMISSIONS.issues.change.issue)
  const canViewOthers = hasPermissions(
    PERMISSIONS.issues.view.othersissue,
    true
  )
  const canArchive = admin
  const canDelete = admin

  const [issueLoading, setIssueLoading] = useState<string | null>(null)

  const getDateFilters = () => {
    const start = formatISO(startOfDay(filter.dateRange.from))
    const end = formatISO(addDays(startOfDay(filter.dateRange.to), 1))

    return {
      startDate: start,
      endDate: end,
    }
  }

  const {
    data,
    loading: queryLoading,
    fetchMore,
  } = useQuery(ALL_ISSUES_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      q: debouncedQuery,
      orderBy: filter.orderBy,
      excludeCategories: debouncedExcludeCategories,
      includeSeverities: filter.severities,
      archivedAtIsNull: !debouncedArchived,
      includeStatuses: debouncedStatuses,
      first: DEFAULT_PAGE_SIZE,
      company: company.id,
      relatedInfringement: filter.relatedInfringement,
      onlyManuallyCreated: filter.onlyManuallyCreated,
      isInvolved:
        filter.involvement.length === 1
          ? filter.involvement[0] === 'INVOLVED'
          : undefined,
      tags: debouncedTags.length > 0 ? debouncedTags : undefined,
      ...(canViewOthers &&
        filter.leaderGroup !== null && {
          leaderGroupId: filter.leaderGroup,
        }),
      ...getDateFilters(),
    },
    onError: onErrorAuto(),
  })
  const [toggleClosed, { loading: closeLoading }] = useMutation<
    ToggleIssueClosedMutation,
    ToggleIssueClosedMutationVariables
  >(TOGGLE_ISSUE_CLOSED_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Sidebar', 'AllIssues'],
    onError: onErrorAuto(),
  })
  const [toggleArchived, { loading: archiveLoading }] = useMutation<
    ToggleIssueArchivedMutation,
    ToggleIssueArchivedMutationVariables
  >(TOGGLE_ISSUE_ARCHIVED_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['Sidebar', 'AllIssues'],
    onError: onErrorAuto(),
  })
  const [deleteIssue, { loading: deleteLoading }] = useMutation(
    DELETE_ISSUE_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: ['Sidebar', 'AllIssues'],
      onError: onErrorAuto(),
    }
  )

  const issues = getNodes(data?.allIssues)
  const pageInfo = data?.allIssues.pageInfo

  const handleScrollBottom = useCallback(async () => {
    if (
      typeof data === 'undefined' ||
      typeof pageInfo === 'undefined' ||
      !pageInfo.hasNextPage
    )
      return
    try {
      await fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [data, fetchMore, pageInfo])

  useInfiniteScroll(
    handleScrollBottom,
    200,
    !queryLoading && pageInfo?.hasNextPage
  )

  async function handleCloseIssue(issue?: IssueNodeForAllIssues) {
    if (typeof issue === 'undefined') return

    if (issue.closed) {
      void executeCloseIssue(issue)()
      return
    }

    const initialData: CloseIssueForm = {
      solution: issue.solution ?? '',
      actualCost:
        typeof issue.actualCost !== 'undefined' && issue.actualCost !== null
          ? issue.actualCost
          : 0,
    }

    const { data } = await addPrompt<CloseIssueForm | null>(resolve => (
      <CloseIssuePrompt initialData={initialData} onResolve={resolve} />
    ))
    if (data === null) return

    void executeCloseIssue(issue)(data)
  }

  function executeCloseIssue(issue?: IssueNodeForAllIssues | null) {
    if (!issue) return () => void 0

    return async (data?: CloseIssueForm) => {
      setIssueLoading(issue.id)

      await toggleClosed({
        variables: {
          id: issue.id,
          closed: !issue.closed,
          solution: data?.solution,
          actualCost: data?.actualCost ?? undefined,
        },
      })
      setIssueLoading(null)
    }
  }

  async function handleArchiveIssue(issue?: IssueNodeForAllIssues) {
    if (typeof issue === 'undefined') return

    setIssueLoading(issue.id)
    await toggleArchived({
      variables: {
        id: issue.id,
        archived: !issue.archived,
      },
    })
    setIssueLoading(null)
  }

  async function handleDeleteIssue(issue?: IssueNodeForAllIssues) {
    if (typeof issue === 'undefined') return

    const { data } = await confirm(
      translations.deleteIssueConfirm,
      translations.deleteIssue
    )
    if (!data) return

    setIssueLoading(issue.id)
    await deleteIssue({
      variables: {
        id: issue.id,
      },
    })
    setIssueLoading(null)
  }

  function createMenuItems(issue: IssueNodeForAllIssues) {
    const items: TableMenuItem<IssueNodeForAllIssues>[] = []

    if (canEdit(issue)) {
      if (!issue.closed && !issue.archived)
        items.push({
          label: translations.editIssue,
          data: issue,
          role: 'link',
          onClick: () =>
            void navigate(`/issues/${issue.id}`, {
              state: {
                edit: true,
              },
            }),
        })
      if (userCanCloseIssue(issue)) {
        items.push({
          label: !issue.closed
            ? translations.closeIssue
            : translations.reopenIssue,
          data: issue,
          onClick: handleCloseIssue,
        })
      }
    }
    if (canArchive)
      items.push({
        label: !issue.archived
          ? translations.archiveIssue
          : translations.restoreIssue,
        data: issue,
        onClick: handleArchiveIssue,
      })
    if (canDelete)
      items.push({
        label: translations.deleteIssue,
        data: issue,
        onClick: handleDeleteIssue,
      })

    return items
  }

  const filterActive =
    debouncedQuery !== '' ||
    debouncedArchived ||
    debouncedStatuses.length > 0 ||
    debouncedExcludeCategories.length !== 0
  const noDataText = filterActive
    ? translations.noResults
    : translations.noIssues
  const mutationLoading = closeLoading || archiveLoading || deleteLoading

  useEffect(() => {
    if (!onFilterUpdate) return

    const amount = data?.allIssues?.total
    onFilterUpdate({ count: amount ?? undefined })
  }, [data?.allIssues?.total, onFilterUpdate])

  return (
    <>
      {isMobile ? (
        <IssuesCards
          issues={issues}
          queryLoading={queryLoading}
          issueLoading={mutationLoading ? issueLoading : null}
          createMenuItems={createMenuItems}
          onLoadMore={handleScrollBottom}
        />
      ) : (
        <Table
          loading={queryLoading}
          loaderProps={{
            columns: !isMobile ? 6 : 1,
            rows: 10,
            expectedWidths: [162, 92, 92, 360, 460, 82],
          }}
          noData={issues.length === 0}
          noDataText={noDataText}
        >
          <thead>
            <tr>
              <SortableHeader
                baseValue="timeOfIssueStart"
                sortValue={filter.orderBy}
                invertDirection
                onSort={onSort}
              >
                {translations.date}
              </SortableHeader>

              <>
                <th>{translations.freightCarrier}</th>

                <th>{translations.reference({ abbreviate: false })}</th>
              </>

              <SortableHeader
                baseValue="user__firstName"
                sortValue={filter.orderBy}
                disabled={!admin}
                onSort={onSort}
              >
                {translations.employee}
              </SortableHeader>

              <SortableHeader
                baseValue="category__name"
                sortValue={filter.orderBy}
                onSort={onSort}
              >
                {translations.category}
              </SortableHeader>
              <SortableHeader
                baseValue="riskAspect__name"
                sortValue={filter.orderBy}
                onSort={onSort}
              >
                {translations.riskAspect}
              </SortableHeader>
              <SortableHeader
                baseValue="severity"
                sortValue={filter.orderBy}
                onSort={onSort}
              >
                {translations.severity}
              </SortableHeader>

              <th>{translations.status}</th>

              <th></th>
            </tr>
          </thead>

          <tbody>
            {issues.map(issue => {
              const menuItems = createMenuItems(issue)

              const licencePlates = issue.vehiclePlateNumber
                ? [issue.vehiclePlateNumber]
                : []

              return (
                <Row key={issue.id} closed={issue.closed}>
                  <IssueDateCell
                    issueId={issue.id}
                    timeOfIssue={issue?.timeOfIssueStart as string}
                  />

                  <VehicleCell plates={licencePlates} />

                  <IssueReferenceCell
                    reference={issue.referenceId}
                    title={issue.title}
                  />

                  <UserCell user={issue.user} />

                  <IssueCategoryCell category={issue.category} />

                  <td>{issue.riskAspect?.name ?? '-'}</td>
                  <td>
                    {issue.severity
                      ? translations.severityGrades[issue.severity]
                      : '-'}
                  </td>

                  <IssueStatusCell
                    closed={issue.closed}
                    archived={issue.archived}
                    edited={issue.edited}
                    editText={translations.started}
                    openText={translations.open}
                    archivedText={translations.archived}
                    closedText={translations.closed}
                  />

                  <TableMenu
                    items={menuItems}
                    loading={mutationLoading && issueLoading === issue.id}
                  />
                </Row>
              )
            })}
          </tbody>
        </Table>
      )}
    </>
  )
}
